import type { MouseEvent } from 'react';
import type { CategoryData } from 'src/types/category-data';
import type { TagData } from 'src/types/tag-data';
import type { Order } from 'src/utils/advisor-dash/client-list/sorting';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SortableTableHead, TableHead, TableHeader, TableRow } from '@savvy-kit/components/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@savvy-kit/components/tooltip';
import { debounce } from 'es-toolkit';
import { Info } from 'lucide-react';
import { DatadogRumAction } from 'src/utils/logging';

import { ARR_TOOLTIP_TEXT } from '@savvy/constants/content-strings/advisor-dashboard';

import type { AdvisorInfo } from './types/advisor-info';
import { SearchField } from './clients-and-prospects/search-field';
import { ClientsActiveFilters } from './clients/client-filters/clients-active-filters';
import { ClientsFilterDropdown } from './clients/client-filters/clients-filter-dropdown';
import { useClientsUrlState, useClientsUrlStateDispatch } from './clients/utils/url-state-hooks';

export enum ColumnHeaderID {
  Name = 'Name',
  Stage = 'XLR8CS__Stage__c',
  EstimatedAUM = 'Estimated_AUM__c',
  LastContactDate = 'Last_Contact_Date__c',
  Owner = 'Owner',
  CreatedDate = 'CreatedDate',
}

export type ColumnHeader = {
  id: string | ColumnHeaderID;
  label: string;
  width: string;
  align?: 'right' | 'left' | 'center';
  numeric?: boolean;
  tooltip?: React.ReactNode;
  sx?: object;
  sortable?: boolean;
};

export function LCDTooltip() {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span className="pl-2">
            <Info className="text-secondary size-4" />
          </span>
        }
      />
      <TooltipContent>
        Last Contact Date includes direct emails between advisors & clients, Dialpad & logged calls and Dialpad SMS.
        Calendar events are NOT included.
      </TooltipContent>
    </Tooltip>
  );
}

export function AnnualRevenueTooltip() {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span className="pl-2">
            <Info className="text-secondary size-4" />
          </span>
        }
      />
      <TooltipContent>{ARR_TOOLTIP_TEXT}</TooltipContent>
    </Tooltip>
  );
}

function AdvisorDashboardTableHead(props: {
  readonly addRightColumn?: boolean;
  readonly advisorInfo?: AdvisorInfo | null;
  readonly columnHeaders: ColumnHeader[];
  readonly displayFilters?: boolean;
  readonly displayPrimaryAdvisorFilter?: boolean;
  readonly rightControls?: React.ReactNode;
  readonly onRequestSort: (e: MouseEvent<unknown>, p: string) => void;
  readonly order: Order;
  readonly orderBy: string;
  readonly tags?: readonly TagData[];
  readonly categories?: readonly CategoryData[];
  readonly showColumnHeaders?: boolean;
}) {
  const urlDispatch = useClientsUrlStateDispatch();

  const { query } = useClientsUrlState();

  const {
    advisorInfo,
    columnHeaders,
    displayFilters = false,
    displayPrimaryAdvisorFilter = false,
    rightControls,
    order,
    orderBy,
    onRequestSort,
    addRightColumn,
    tags,
    categories,
    showColumnHeaders = true,
  } = props;

  const [localQuery, setLocalQuery] = useState(query ?? '');

  useEffect(() => {
    setLocalQuery(query ?? '');
  }, [query]);

  const urlDispatchRef = useRef(urlDispatch);
  urlDispatchRef.current = urlDispatch;

  const debouncedUrlDispatch = useMemo(
    () =>
      debounce((queryValue: string) => {
        urlDispatchRef.current({
          type: 'setQueryFilter',
          query: queryValue,
        });
      }, 300),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedUrlDispatch.cancel();
    };
  }, [debouncedUrlDispatch]);

  const createSortHandler = (property: string) => (event: MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const newValue = e.target.value;
    setLocalQuery(newValue);
    debouncedUrlDispatch(newValue);
  };

  return (
    <TableHeader>
      {displayFilters && (
        <TableRow className="hover:bg-transparent">
          <td colSpan={12} className="px-3 py-2">
            <div className="flex flex-row gap-4 items-center justify-between">
              <div className="flex flex-row gap-4">
                <SearchField
                  datadogRumAction={DatadogRumAction.HouseholdSearch}
                  value={localQuery}
                  onChange={handleQueryChange}
                />
                <ClientsFilterDropdown
                  advisorInfo={advisorInfo}
                  displayPrimaryAdvisorFilter={displayPrimaryAdvisorFilter}
                  tags={tags}
                  categories={categories}
                />
              </div>
              {rightControls}
            </div>

            <ClientsActiveFilters tags={tags} categories={categories} advisorInfo={advisorInfo} />
          </td>
        </TableRow>
      )}
      {showColumnHeaders && (
        <TableRow className="hover:bg-transparent">
          {columnHeaders.map((header: ColumnHeader) => {
            const isSortable = header.sortable !== false;
            const isSorted = orderBy === header.id;
            const sortDirection: 'ASC' | 'DESC' | undefined = (() => {
              if (isSorted) {
                if (order === 'asc') {
                  return 'ASC';
                }

                return 'DESC';
              }

              return undefined;
            })();

            const headerStyle = { width: header.width, ...(header.sx ?? {}) };

            return isSortable ? (
              <SortableTableHead
                key={header.id}
                style={headerStyle}
                isSorted={isSorted}
                sortDirection={sortDirection}
                onClick={createSortHandler(header.id)}
              >
                {header.label}
                {header.tooltip}
              </SortableTableHead>
            ) : (
              <TableHead key={header.id} style={headerStyle}>
                <span className="text-secondary font-medium">
                  {header.label}
                  {header.tooltip}
                </span>
              </TableHead>
            );
          })}
          {addRightColumn && <TableHead className="text-right" />}
        </TableRow>
      )}
    </TableHeader>
  );
}

export default AdvisorDashboardTableHead;
